<script setup>
  import {computed, inject, ref, watch} from "vue";

  import ComponentUnsavedChanges from "@components/Alerts/UnsavedChanges.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {useForm} from "@inertiajs/vue3";
  import {degreeOfCareLevel} from "@pages/Patients/Components/PatientAttributes/Enums.js";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientAttributesDegreeOfCareAttribute",
  });

  const props = defineProps({
    attribute: {
      type: Object,
      default: () => ({}),
    },
    patient: {
      type: Object,
      default: () => ({}),
    },
    recentlyAdded: {
      type: Boolean,
      default: false,
    },
  });
  const broadcast = inject("$broadcast");

  const refConfirmationDialog = ref(null);

  const activeEdit = ref(props.recentlyAdded);
  const form = useForm({
    type: props.attribute.type,
    payload: {
      level: props.attribute?.payload?.level,
    },
  });

  const helperText = computed(() => {
    return degreeOfCareLevel[form.payload.level] !== undefined ? degreeOfCareLevel[form.payload.level] : null;
  });

  watch(
    () => props.recentlyAdded,
    (newValue) => {
      activeEdit.value = newValue;
    },
  );

  function cancelEdit() {
    // was recently added AND was NOT saved once
    if (props.recentlyAdded && !form.wasSuccessful) {
      remove();
    } else {
      form.reset();
      activeEdit.value = false;
    }
  }

  function save() {
    form.put(route("attributes.update", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        activeEdit.value = false;
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }

  function confirmRemove() {
    refConfirmationDialog.value.open();
  }

  function remove() {
    form.delete(route("attributes.destroy", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }
</script>

<template>
  <form :data-test="getTestId('degree-of-care-attribute-form')" @submit.prevent="save">
    <ComponentCard class="group relative hover:bg-gray-100" :test-id="getTestId('degree-of-care-attribute-form')">
      <div class="flex items-baseline justify-between">
        <h6 class="text-xl">Pflegestufe</h6>

        <MedSpinner v-if="form.processing" size="xs" :data-test="getTestId('degree-of-care-attribute-spinner')" />

        <ComponentUnsavedChanges v-else-if="form.isDirty || form.recentlySuccessful" :form="form" />

        <span v-else class="text-xs text-gray-500">Aktualisiert {{ attribute.updated_at }}</span>
      </div>

      <template v-if="activeEdit">
        <div class="mt-4 grid grid-cols-4 gap-8">
          <ComponentSelect
            v-model="form.payload.level"
            class="col-span-2"
            label="Pflegegrad"
            :validation="form.errors['payload.level']"
            :helper-text="helperText"
            :test-id="getTestId('degree-of-care-attribute-form-card-payload-level')"
          >
            <option value="1">Pflegegrad 1</option>
            <option value="2">Pflegegrad 2</option>
            <option value="3">Pflegegrad 3</option>
            <option value="4">Pflegegrad 4</option>
            <option value="5">Pflegegrad 5</option>
          </ComponentSelect>
        </div>
      </template>

      <template v-else>
        <div class="mt-0.5 flex min-h-4 items-baseline space-x-4">
          <span class="text-3xl">{{ attribute.payload?.level }}</span>
          <span v-text="helperText" />
        </div>

        <div class="absolute right-2 bottom-2 flex gap-2">
          <MedButton
            aria-label="Pflegestufe bearbeiten"
            flat
            variant="primary"
            icon="edit"
            rounded="full"
            :data-test="getTestId('degree-of-care-attribute-form-card-edit-button')"
            @click="activeEdit = true"
          />

          <MedButton
            aria-label="Pflegestufe löschen"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('degree-of-care-attribute-form-card-delete-button')"
            @click="confirmRemove()"
          />
        </div>
      </template>

      <template v-if="activeEdit" #actions>
        <MedButton
          type="submit"
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('degree-of-care-attribute-form-card-save-button')"
        >
          Speichern
        </MedButton>

        <MedButton
          :disabled="form.processing"
          :data-test="getTestId('degree-of-care-attribute-form-card-cancel-button')"
          @click="cancelEdit()"
        >
          Abbrechen
        </MedButton>
      </template>
    </ComponentCard>
  </form>

  <ComponentConfirmationDialog
    ref="refConfirmationDialog"
    title="Sind Sie sicher?"
    content="Die Angabe „Pflegestufe“ wird gelöscht."
    @confirmed="remove()"
  />
</template>
